"use client";
import { AlertType } from "components/Common/alert-dialog-page";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

interface AlertContextType {
  isOpen: boolean;
  title: string;
  message: string;
  type: AlertType;
  show: (
    title: string,
    message: string,
    type: AlertType,
    onOK?: () => void,
    onClose?: () => void
  ) => void;
  hide: () => void;
  onOK?: () => Promise<void> | void;
  onClose?: () => Promise<void> | void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [onOK, setOnOK] = useState<(() => Promise<void> | void) | undefined>(
    undefined
  );
  const [onClose, setOnClose] = useState<
    (() => Promise<void> | void) | undefined
  >(undefined);
  const [type, setType] = useState<AlertType>("alert");

  const show = useCallback(
    (
      title: string,
      message: string,
      type: AlertType = "alert",
      onOK?: () => Promise<void> | void,
      onClose?: () => Promise<void> | void
    ) => {
      setMessage(message);
      setTitle(title);
      setType(type);
      setIsOpen(true);
      storeOnOK(onOK);
      storeOnClose(onClose);
    },
    []
  );

  // 할당시 호출되는 것을 방지하기 위해 함수로 감싸줌
  const storeOnOK = (onOKFunc?: () => void) => {
    setOnOK(() => onOKFunc);
  };

  const storeOnClose = (onCloseFunc?: () => void) => {
    setOnClose(() => onCloseFunc);
  };

  const hide = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <AlertContext.Provider
      value={{ isOpen, title, message, type, show, hide, onOK, onClose }}
    >
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};
